import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import type { ECharts } from 'echarts';
import { useTranslation } from 'react-i18next';
import type { TimelineDataPoint } from './timelineUtils';
import { MENTION_TYPE_COLORS, MENTION_TYPE_LABELS } from './timelineUtils';

interface SocialGraphTimelineProps {
  /** buckets inside the current overview-strip window (the chart is zoomed to it) */
  data: TimelineDataPoint[];
  /** series left visible by the header legend filters */
  visibleTypes: string[];
  /** selected day, as an index into `data`; a click on a bar picks a day —
   *  clearing happens OUTSIDE the chart (Reset / click outside the timeline),
   *  handled by the page */
  selectedIndex: number | null;
  onSelectDay: (index: number) => void;
  onHoverPeriod: (date: Date | null) => void;
  /** playback frame — moves the axis tooltip to this bucket while playing */
  activeIndex: number | null;
}

// selected-day bar + its top dot (Figma Timeline / Panel state "Selected")
const SELECTED_BAR_COLOR = '#2463eb';
const SELECTED_DOT_COLOR = '#7da6f7';

// Figma Timeline / Tooltip 502-12
const DARK_TOOLTIP = {
  backgroundColor: '#1f1f24',
  borderColor: '#3a3a42',
  borderWidth: 1,
  padding: [6, 9, 7, 9] as [number, number, number, number],
  borderRadius: 6,
  textStyle: { color: '#e4e4e7', fontSize: 12 },
  extraCssText: 'box-shadow: 0 1px 3px rgba(0,0,0,0.4), 0 6px 20px rgba(0,0,0,0.32);',
};

// bucket labels originate from backend time_buckets keys (parseTimeBucketKey
// tolerates unrecognized keys) — escape before injecting into the tooltip's
// raw HTML string
const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (char) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] as string,
  );

export const SocialGraphTimeline: React.FC<SocialGraphTimelineProps> = ({
  data,
  visibleTypes,
  selectedIndex,
  onSelectDay,
  onHoverPeriod,
  activeIndex,
}) => {
  const { t } = useTranslation();
  const chartRef = useRef<ReactECharts>(null);

  const allZero = useMemo(
    () =>
      data.every((point) =>
        visibleTypes.every((type) => (point.byType[type] ?? 0) === 0),
      ),
    [data, visibleTypes],
  );

  const tooltipFormatter = useCallback(
    (params: any) => {
      const items = Array.isArray(params) ? params : [params];
      const bars = items.filter((item: any) => item.seriesType === 'bar');
      if (!bars.length) return '';
      const total = bars.reduce((sum: number, item: any) => sum + (item.value ?? 0), 0);
      // per-series breakdown in one horizontal row, legend order (Figma 502-16)
      const series = bars
        .map(
          (item: any) =>
            `<span style="display:inline-flex;align-items:center;gap:5px;">` +
            `<span style="width:7px;height:7px;border-radius:2px;background:${item.color};"></span>` +
            `<span style="color:#a1a1aa;">${item.seriesName}</span>` +
            `<span style="color:#fff;font-weight:500;">${item.value ?? 0}</span>` +
            `</span>`,
        )
        .join('');
      return (
        `<div style="font-size:12px;line-height:16px;letter-spacing:0.25px;">` +
        `<div style="display:flex;align-items:center;justify-content:space-between;gap:16px;font-weight:500;">` +
        `<span style="color:#fff;">${escapeHtml(String(bars[0].name ?? ''))}</span>` +
        `<span style="color:#a1a1aa;">${t('Total')} ${total}</span>` +
        `</div>` +
        `<div style="display:flex;gap:12px;margin-top:5px;">${series}</div>` +
        `</div>`
      );
    },
    [t],
  );

  const option = useMemo(() => ({
    tooltip: {
      ...DARK_TOOLTIP,
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      formatter: tooltipFormatter,
    },
    grid: {
      top: 10,
      left: 40,
      right: 12,
      bottom: 26,
    },
    xAxis: {
      type: 'category' as const,
      data: data.map(d => d.label),
      axisLabel: { color: '#84848d', fontSize: 10 },
      axisLine: { lineStyle: { color: '#28282e' } },
      axisTick: { lineStyle: { color: '#28282e' } },
    },
    yAxis: {
      type: 'value' as const,
      // the designed empty state still draws a 0–1 scale under the message
      max: allZero ? 1 : undefined,
      axisLabel: { color: '#84848d', fontSize: 10 },
      splitLine: { lineStyle: { color: '#28282e' } },
      axisLine: { show: false },
    },
    // brush selection was replaced by the overview strip; the toolbox must stay
    // explicitly hidden — echarts-for-react setOption merges, so a lingering
    // toolbox from an earlier option would re-render its icons over the canvas
    toolbox: { show: false },
    series: [
      ...visibleTypes.map(type => ({
        name: t(MENTION_TYPE_LABELS[type] ?? type),
        type: 'bar' as const,
        stack: 'total',
        data: data.map((d, i) => ({
          value: d.byType[type] ?? 0,
          itemStyle: i === selectedIndex ? { color: SELECTED_BAR_COLOR } : undefined,
        })),
        itemStyle: { color: MENTION_TYPE_COLORS[type] ?? '#7C8597' },
        barMaxWidth: 24,
      })),
      // dot capping the selected bar
      {
        name: 'selected-day-dot',
        type: 'scatter' as const,
        silent: true,
        tooltip: { show: false },
        symbolSize: 6,
        itemStyle: { color: SELECTED_DOT_COLOR },
        data:
          selectedIndex !== null && data[selectedIndex]
            ? [[
                selectedIndex,
                visibleTypes.reduce(
                  (sum, type) => sum + (data[selectedIndex].byType[type] ?? 0),
                  0,
                ),
              ]]
            : [],
      },
    ],
  }), [data, visibleTypes, selectedIndex, allZero, tooltipFormatter, t]);

  const handleClick = useCallback((params: any) => {
    if (params.componentType === 'series' && params.seriesType === 'bar') {
      onSelectDay(params.dataIndex);
    }
  }, [onSelectDay]);

  const handleMouseOver = useCallback((params: any) => {
    const idx = params.dataIndex;
    if (idx != null && idx >= 0 && idx < data.length) {
      onHoverPeriod(data[idx].date);
    }
  }, [data, onHoverPeriod]);

  const handleMouseOut = useCallback(() => {
    onHoverPeriod(null);
  }, [onHoverPeriod]);

  // walk the axis tooltip (and its shadow pointer) along with playback so the
  // user sees which bucket the graph is currently filtered to
  useEffect(() => {
    const chart = chartRef.current?.getEchartsInstance() as ECharts | undefined;
    if (!chart) return;
    if (activeIndex !== null && activeIndex >= 0 && activeIndex < data.length) {
      chart.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: activeIndex });
    } else {
      chart.dispatchAction({ type: 'hideTip' });
    }
  }, [activeIndex, data.length]);

  const events = useMemo(() => ({
    click: handleClick,
    mouseover: handleMouseOver,
    mouseout: handleMouseOut,
  }), [handleClick, handleMouseOver, handleMouseOut]);

  return (
    <ReactECharts
      ref={chartRef}
      option={option}
      // the legend filters add/remove whole series — a merged setOption would
      // keep the removed ones rendering forever
      notMerge
      onEvents={events}
      opts={{ renderer: 'svg' }}
      style={{ width: '100%', height: '100%' }}
    />
  );
};

SocialGraphTimeline.displayName = 'SocialGraphTimeline';
